import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { SortingRule } from 'react-table';

import usePaginatedTeamProjects from '../../../../../hooks/api/teams/useTeamProjects';
import usePaginatedTeamReleaseGroups from '../../../../../hooks/api/teams/useTeamReleaseGroups';
import type { GetTeamResponse } from '../../../../../../shared/types/routes/teams';

import BaseTeamProjectsTable from './BaseTeamProjectsTable';

type ContainerProps = {
  team: GetTeamResponse;
  search: string;
  isShowingAddProjectsModal: boolean;
  onHideAddProjectsModal: () => void;
  // eslint-disable-next-line react/no-unused-prop-types -- This isn't true. It's used in the TeamProjectsContainer.
  mode: 'projects' | 'releaseGroups';
};

const TeamProjectWrapper: VoidFunctionComponent<ContainerProps> = ({
  team,
  search,
  isShowingAddProjectsModal,
  onHideAddProjectsModal,
}) => {
  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'title', desc: false });

  //  Queries
  const {
    data,
    isLoading: isLoadingProjects,
    isFetching: isFetchingProjects,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = usePaginatedTeamProjects(team.id, { search, sort: sortMode.desc ? 'desc' : 'asc' });

  return (
    <BaseTeamProjectsTable
      team={team}
      search={search}
      isShowingAddProjectsModal={isShowingAddProjectsModal}
      onHideAddProjectsModal={onHideAddProjectsModal}
      rows={{
        page,
        pageSize,
        totalCount: data?.totalCount ?? 0,
        results: data?.results.map((p) => ({
          ...p,
          type: 'project',
        })) ?? [],
      }}
      page={page}
      setPage={setPage}
      pageSize={pageSize}
      setPageSize={setPageSize}
      isLoading={isLoadingProjects}
      isFetching={isFetchingProjects}
      sortMode={sortMode}
      setSortMode={setSortMode}
    />
  );
};

const TeamReleaseGroupWrapper: VoidFunctionComponent<ContainerProps> = ({
  team,
  search,
  isShowingAddProjectsModal,
  onHideAddProjectsModal,
}) => {
  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'title', desc: false });

  //  Queries
  const {
    data,
    isLoading: isLoadingReleaseGroups,
    isFetching: isFetchingReleaseGroups,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = usePaginatedTeamReleaseGroups(team.id, { search, sort: sortMode.desc ? 'desc' : 'asc' });

  return (
    <BaseTeamProjectsTable
      team={team}
      search={search}
      isShowingAddProjectsModal={isShowingAddProjectsModal}
      onHideAddProjectsModal={onHideAddProjectsModal}
      rows={{
        page,
        pageSize,
        totalCount: data?.totalCount ?? 0,
        results: data?.results.map((p) => ({
          ...p,
          type: 'releaseGroup',
        })) ?? [],
      }}
      page={page}
      setPage={setPage}
      pageSize={pageSize}
      setPageSize={setPageSize}
      isLoading={isLoadingReleaseGroups}
      isFetching={isFetchingReleaseGroups}
      sortMode={sortMode}
      setSortMode={setSortMode}
    />
  );
};

const TeamProjectsContainer: VoidFunctionComponent<ContainerProps> = ({
  team,
  search,
  isShowingAddProjectsModal,
  onHideAddProjectsModal,
  mode,
}) => {
  if (mode === 'projects') {
    return (
      <TeamProjectWrapper
        team={team}
        search={search}
        isShowingAddProjectsModal={isShowingAddProjectsModal}
        onHideAddProjectsModal={onHideAddProjectsModal}
        mode={mode}
      />
    );
  }

  return (
    <TeamReleaseGroupWrapper
      team={team}
      search={search}
      isShowingAddProjectsModal={isShowingAddProjectsModal}
      onHideAddProjectsModal={onHideAddProjectsModal}
      mode={mode}
    />
  );
};

export default TeamProjectsContainer;
